/**
 * FullCharacterSheet
 * Complete character sheet with all details - skills, spells, inventory, features, etc.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { Character } from '@machina/core';
import { getSpell, parseSpellAttackType, parseSpellSaveType, parseSpellDamage, scaleCantripDamage, parseSpellHealing, getEchoMadnessTier, resolveSpellDamage } from '@machina/core';
import { getCharacterWeapons, getWeaponAttackBonus, getWeaponDamageNotation, getWeaponAbilityMod, getMojoEffect, getMojoTier, applyReputationBonus, getPersonalReputationTier, getInventorTier, SOCIAL_SKILLS, getXpProgress } from '@machina/core';
import type { SaveAbility, SpellDamage, SpellHealing } from '@machina/core';
import { useCharacterStore } from '../stores/characterStore';
import { LevelUpWizard } from './LevelUpWizard';
import { useDiceStore } from '../stores/diceStore';
import { useChatStore } from '../stores/chatStore';
import { useTokenStore } from '../stores/tokenStore';
import { useCombatStore } from '../stores/combatStore';
import { useInitiativeStore } from '../stores/initiativeStore';
import { Icon, type IconName } from './Icon';
import { PlayerNotesModal } from './PlayerNotesModal';
import { ImageGallery } from './ImageGallery';
import type { ImagePurpose } from '@machina/core';

// =============================================================================
// CONSTANTS
// =============================================================================

const CLASS_ICONS: Record<string, IconName> = {
  Fighter: 'sword',
  Wizard: 'wand',
  Ranger: 'target',
  Cleric: 'sparkle',
  Rogue: 'skull',
  Barbarian: 'attack',
  Paladin: 'shield',
  Warlock: 'moon',
  Sorcerer: 'crystal',
  Bard: 'horn',
  Druid: 'heart',
  Monk: 'star',
};

// D&D 5e skill list with associated abilities
const SKILLS: { name: string; ability: 'strength' | 'dexterity' | 'constitution' | 'intelligence' | 'wisdom' | 'charisma' }[] = [
  { name: 'Acrobatics', ability: 'dexterity' },
  { name: 'Animal Handling', ability: 'wisdom' },
  { name: 'Arcana', ability: 'intelligence' },
  { name: 'Athletics', ability: 'strength' },
  { name: 'Deception', ability: 'charisma' },
  { name: 'History', ability: 'intelligence' },
  { name: 'Insight', ability: 'wisdom' },
  { name: 'Intimidation', ability: 'charisma' },
  { name: 'Investigation', ability: 'intelligence' },
  { name: 'Medicine', ability: 'wisdom' },
  { name: 'Nature', ability: 'intelligence' },
  { name: 'Perception', ability: 'wisdom' },
  { name: 'Performance', ability: 'charisma' },
  { name: 'Persuasion', ability: 'charisma' },
  { name: 'Religion', ability: 'intelligence' },
  { name: 'Sleight of Hand', ability: 'dexterity' },
  { name: 'Stealth', ability: 'dexterity' },
  { name: 'Survival', ability: 'wisdom' },
];

// =============================================================================
// HELPERS
// =============================================================================

function getClassIcon(classDisplay: string): IconName {
  for (const [className, icon] of Object.entries(CLASS_ICONS)) {
    if (classDisplay.includes(className)) return icon;
  }
  return 'token';
}

function getModifier(score: number): number {
  return Math.floor((score - 10) / 2);
}

function formatModifier(mod: number): string {
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

// =============================================================================
// SECTION COMPONENTS
// =============================================================================

function Section({ title, icon, children, accentColor = 'var(--color-accent)' }: {
  title: string;
  icon?: IconName;
  children: React.ReactNode;
  accentColor?: string;
}) {
  return (
    <div style={{
      padding: '16px',
      marginBottom: '12px',
      background: 'linear-gradient(180deg, rgba(20, 15, 30, 0.6) 0%, rgba(10, 8, 15, 0.8) 100%)',
      borderRadius: '4px',
      border: '1px solid rgba(100, 80, 120, 0.2)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
        paddingBottom: 10,
        borderBottom: '1px solid rgba(100, 80, 120, 0.15)',
      }}>
        {icon && (
          <div style={{
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: `${accentColor}20`,
          }}>
            <Icon name={icon} size={12} color={accentColor} />
          </div>
        )}
        <span style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: 'rgba(180, 160, 200, 0.8)',
          fontFamily: 'serif',
        }}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

function StatBox({ label, value, subValue }: { label: string; value: string | number; subValue?: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px 12px' }}>
      <div style={{
        fontSize: 24,
        fontWeight: 300,
        fontFamily: 'var(--font-display)',
        color: 'var(--color-text)',
      }}>
        {value}
      </div>
      {subValue && (
        <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
          {subValue}
        </div>
      )}
      <div style={{
        fontSize: 9,
        color: 'var(--color-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginTop: 4,
      }}>
        {label}
      </div>
    </div>
  );
}

// =============================================================================
// ECHO KNIGHT CONTROLS
// =============================================================================

function EchoKnightControls({ character }: { character: Character }) {
  const addToken = useTokenStore((s) => s.addToken);
  const removeToken = useTokenStore((s) => s.removeToken);
  const tokens = useTokenStore((s) => s.tokens);
  const updateCharacter = useCharacterStore((s) => s.updateCharacter);

  // Find this character's token on the map
  const characterToken = useTokenStore.getState().getTokenByCombatantId(character.id);

  // Find existing echo token for this character
  const echoName = `Echo (${character.name})`;
  const existingEcho = Array.from(tokens.values()).find(
    (t) => t.tokenType === 'echo' && t.name === echoName
  );

  // Track voluntary dismissals to distinguish from combat kills
  const dismissedRef = useRef(false);
  const prevEchoRef = useRef<boolean>(!!existingEcho);

  // Detect echo death (echo vanishes without voluntary dismiss/resummon)
  useEffect(() => {
    const hadEcho = prevEchoRef.current;
    const hasEcho = !!existingEcho;
    prevEchoRef.current = hasEcho;

    if (hadEcho && !hasEcho && !dismissedRef.current) {
      // Echo was killed in combat — increment echoMadness
      const current = character.echoMadness ?? 0;
      updateCharacter(character.id, { echoMadness: current + 1 });
    }

    // Reset dismissed flag after processing
    dismissedRef.current = false;
  }, [existingEcho, character.id, character.echoMadness, updateCharacter]);

  const handleManifestEcho = () => {
    // Remove old echo if exists (only 1 at a time) — voluntary resummon
    if (existingEcho) {
      dismissedRef.current = true;
      removeToken(existingEcho.id);
    }

    // Find character's token position (fallback to center of map)
    const charToken = useTokenStore.getState().getTokenByCombatantId(character.id);
    const gridSize = useTokenStore.getState().gridSize;
    const basePos = charToken
      ? { x: charToken.position.x + gridSize, y: charToken.position.y }
      : { x: gridSize * 5, y: gridSize * 5 };

    // AC = 14 + proficiency bonus (per Echo Knight rules)
    const echoAC = 14 + character.proficiencyBonus;

    addToken({
      name: echoName,
      position: basePos,
      tokenType: 'echo',
      currentHp: 1,
      maxHp: 1,
      ac: echoAC,
      ownerId: character.id,
      visuals: {
        shape: 'circle',
        size: 'medium',
        color: '#7dd3fc',
        showHpBar: false,
        showConditionIcons: false,
      },
    });
  };

  const handleDismissEcho = () => {
    if (existingEcho) {
      dismissedRef.current = true;
      removeToken(existingEcho.id);
    }
  };

  // Echo Madness display
  const echoMadness = character.echoMadness ?? 0;
  const madnessTier = getEchoMadnessTier(echoMadness);

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>
        Echo Knight
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={handleManifestEcho}
          style={{
            flex: 1,
            padding: '10px 10px',
            background: existingEcho
              ? 'linear-gradient(180deg, rgba(60, 80, 100, 0.4) 0%, rgba(40, 55, 70, 0.6) 100%)'
              : 'linear-gradient(180deg, rgba(50, 140, 180, 0.4) 0%, rgba(30, 90, 120, 0.6) 100%)',
            color: '#7dd3fc',
            border: '1px solid rgba(125, 211, 252, 0.4)',
            borderRadius: '2px',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
          title={existingEcho ? 'Resummon echo at current position' : 'Summon echo adjacent to your token'}
        >
          <Icon name="ghost" size={14} color="#7dd3fc" />
          {existingEcho ? 'Resummon Echo' : 'Manifest Echo'}
        </button>
        {existingEcho && (
          <button
            onClick={handleDismissEcho}
            style={{
              padding: '10px 14px',
              background: 'linear-gradient(180deg, rgba(80, 60, 60, 0.4) 0%, rgba(50, 35, 35, 0.6) 100%)',
              color: 'rgba(200, 180, 180, 0.8)',
              border: '1px solid rgba(150, 120, 120, 0.3)',
              borderRadius: '2px',
              cursor: 'pointer',
              fontSize: 11,
            }}
            title="Dismiss your echo"
          >
            Dismiss
          </button>
        )}
      </div>
      {existingEcho && characterToken && (
        <div style={{
          marginTop: 6,
          fontSize: 9,
          color: 'rgba(125, 211, 252, 0.6)',
          fontStyle: 'italic',
        }}>
          Echo active — AC {14 + character.proficiencyBonus}, 1 HP
          {echoMadness > 0 && (
            <span style={{ marginLeft: 8, color: madnessTier?.color ?? '#7dd3fc' }}>
              | Echo Disturbance: {echoMadness} ({madnessTier?.name})
            </span>
          )}
        </div>
      )}
      {/* Echo Disturbance indicator (visible to player when score >= 1) */}
      {madnessTier && (
        <div style={{
          marginTop: 8,
          padding: '6px 10px',
          background: `${madnessTier.color}10`,
          border: `1px solid ${madnessTier.color}30`,
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <div style={{
            fontSize: 9,
            color: 'var(--color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            whiteSpace: 'nowrap',
          }}>
            Echo Disturbance
          </div>
          {/* Level dots */}
          <div style={{ display: 'flex', gap: 3, flex: 1 }}>
            {Array.from({ length: Math.min(echoMadness, 12) }, (_, i) => (
              <div
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: madnessTier.color,
                  opacity: 0.4 + (i / 12) * 0.6,
                }}
              />
            ))}
          </div>
          <div style={{
            fontSize: 9,
            color: madnessTier.color,
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
          }}>
            {madnessTier.playerLabel}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface FullCharacterSheetProps {
  character: Character;
}

export function FullCharacterSheet({ character }: FullCharacterSheetProps) {
  const updateCharacter = useCharacterStore((s) => s.updateCharacter);
  const damageCharacter = useCharacterStore((s) => s.damageCharacter);
  const healCharacter = useCharacterStore((s) => s.healCharacter);

  // Dice rolling — use selectors to avoid re-renders from lastResult changes
  const roll = useDiceStore((s) => s.roll);
  const isReady = useDiceStore((s) => s.isReady);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);
  const addSpellLookup = useChatStore((s) => s.addSpellLookup);
  const addSavePrompt = useChatStore((s) => s.addSavePrompt);
  const addMessage = useChatStore((s) => s.addMessage);

  // For NPCs, use the character name as sender (not "The Deep")
  const senderName = character.isNpc ? character.name : (character.playerName || 'Player');

  // Player notes
  const [showNotes, setShowNotes] = useState(false);
  const handleSaveNotes = useCallback((text: string) => {
    updateCharacter(character.id, { playerNotes: text });
  }, [character.id, updateCharacter]);

  // Spell casting state
  const [castingSpell, setCastingSpell] = useState<string | null>(null);

  // Spell info cache: maps spell name -> attack type, save type, damage, and/or healing
  const [spellInfoCache, setSpellInfoCache] = useState<Record<string, {
    attackType?: 'ranged' | 'melee';
    saveType?: { ability: SaveAbility };
    damage?: SpellDamage;
    healing?: SpellHealing;
    isCantrip?: boolean;
  }>>({});

  // Total character level (for cantrip scaling)
  const characterLevel = character.classes.reduce((sum, c) => sum + c.level, 0);

  // Echo Madness effects — silently baked into rolls
  const isEchoKnight = character.classes.some(c => c.subclass === 'Echo Knight');
  const echoMadnessTier = isEchoKnight ? getEchoMadnessTier(character.echoMadness ?? 0) : null;
  const echoEffects = echoMadnessTier?.effects ?? null;

  // Character score effects — silently baked into rolls
  const charName = character.name?.toLowerCase() ?? '';
  const isMojoChar = charName.includes('juju');
  const mojoScore = character.mojo ?? 0;
  const mojoEffect = isMojoChar ? getMojoEffect(mojoScore) : null;
  const isReputationChar = charName.includes('skolnit');
  const reputationScore = character.reputation ?? 0;

  // Preload spell info (attack types, save types, damage, healing) for known/prepared spells
  useEffect(() => {
    if (!character.spellcasting) return;
    // Check both known (cantrips) and prepared spells
    const spells = [...new Set([...character.spellcasting.knownSpells, ...character.spellcasting.preparedSpells])];
    if (spells.length === 0) return;

    let cancelled = false;
    const cache: Record<string, {
      attackType?: 'ranged' | 'melee';
      saveType?: { ability: SaveAbility };
      damage?: SpellDamage;
      healing?: SpellHealing;
      isCantrip?: boolean;
    }> = {};

    Promise.all(
      spells.map(async (spellName) => {
        try {
          const slug = spellName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          const spell = await getSpell(slug);
          const attackType = parseSpellAttackType(spell.desc);
          const saveType = parseSpellSaveType(spell.desc);
          const baseDamage = parseSpellDamage(spell.desc);
          const healing = parseSpellHealing(spell.desc);
          const isCantrip = spell.level_int === 0;

          if (attackType || saveType || baseDamage || healing) {
            cache[spellName] = {
              ...(attackType ? { attackType } : {}),
              ...(saveType ? { saveType } : {}),
              ...(baseDamage ? { damage: isCantrip ? scaleCantripDamage(baseDamage, characterLevel) : baseDamage } : {}),
              ...(healing ? { healing } : {}),
              isCantrip,
            };
          }
        } catch {
          // Spell lookup failed — skip
        }
      })
    ).then(() => {
      if (!cancelled) setSpellInfoCache(cache);
    });

    return () => { cancelled = true; };
  }, [character.spellcasting, characterLevel]);

  // Attack roll mode toggle
  const [attackRollMode, setAttackRollMode] = useState<'disadvantage' | 'normal' | 'advantage'>('normal');

  const hpPercent = Math.round((character.currentHp / character.maxHp) * 100);
  const tokenColor = character.tokenColor || 'var(--color-accent)';

  // Initiative — check current value from initiative store
  const initEntry = useInitiativeStore((s) => s.entries.find(e => e.characterId === character.id));
  const currentInitiative = initEntry?.initiative ?? 0;

  const handleInitiativeRoll = async () => {
    if (!isReady) return;
    const dexMod = getModifier(character.abilityScores.dexterity);
    const result = await roll('1d20');
    if (result) {
      const total = result.total + dexMod;
      useCombatStore.getState().setInitiative(character.id, total, dexMod);
      useInitiativeStore.getState().setInitiative(`char-${character.id}`, total);
      addDiceRoll({
        sender: senderName,
        characterName: character.name,
        notation: `1d20+${dexMod}`,
        result: { ...result, notation: `1d20+${dexMod}`, total },
        rollType: 'normal',
        rollLabel: 'initiative',
      });
    }
  };

  // Handle dice roll and post to chat
  const handleRoll = async (
    notation: string,
    label: string,
    rollType: 'normal' | 'advantage' | 'disadvantage' = 'normal',
    rollLabel?: 'save' | 'check' | 'damage' | 'attack' | null
  ) => {
    if (!isReady) return;

    // Silently apply reputation bonus on social skill checks (probabilistic)
    let reputationBonusApplied = 0;
    if (isReputationChar && rollLabel === 'check' && (SOCIAL_SKILLS as readonly string[]).includes(label)) {
      const { bonus, chance } = applyReputationBonus(reputationScore);
      if (bonus > 0 && Math.random() * 100 < chance) {
        reputationBonusApplied = bonus;
      }
    }

    if (rollType !== 'normal' && notation.match(/^\d*d20$/i)) {
      // Roll 2d20 in one throw for advantage/disadvantage
      const result = await roll('2d20');
      if (!result) return;

      const val1 = result.rolls[0]?.value ?? 0;
      const val2 = result.rolls[1]?.value ?? 0;
      const chosen = rollType === 'advantage' ? Math.max(val1, val2) : Math.min(val1, val2);

      addDiceRoll({
        sender: senderName,
        characterName: character.name,
        notation: `${notation} (${label})`,
        result: {
          notation: `${notation} (${label})`,
          rolls: result.rolls,
          total: chosen + reputationBonusApplied,
        },
        rollType,
        rollLabel: rollLabel || undefined,
      });
    } else {
      const result = await roll(notation);
      if (result) {
        addDiceRoll({
          sender: senderName,
          characterName: character.name,
          notation: `${notation} (${label})`,
          result: {
            notation: `${notation} (${label})`,
            rolls: result.rolls,
            total: result.total + reputationBonusApplied,
          },
          rollLabel: rollLabel || undefined,
        });
      }
    }
  };

  // Handle attack roll (supports advantage/disadvantage)
  const handleAttackRoll = async (attackBonus: number, label: string) => {
    if (!isReady) return;

    // Silently apply echo madness attack penalty + mojo attack bonus
    const effectiveBonus = attackBonus + (echoEffects?.attackModifier ?? 0) + (mojoEffect?.attackBonus ?? 0);

    // Mojo cap helper: clamp die value if cap exists
    const applyMojoCap = (die: number) => mojoEffect?.cap != null ? Math.min(die, mojoEffect.cap) : die;

    const mode = attackRollMode;

    if (mode !== 'normal') {
      // Roll 2d20 in one throw for advantage/disadvantage
      const result = await roll('2d20');
      if (!result) return;

      const rawDie1 = result.rolls[0]?.value ?? 0;
      const rawDie2 = result.rolls[1]?.value ?? 0;
      const die1 = applyMojoCap(rawDie1);
      const die2 = applyMojoCap(rawDie2);
      const chosenDie = mode === 'advantage' ? Math.max(die1, die2) : Math.min(die1, die2);
      const discardedDie = mode === 'advantage' ? Math.min(die1, die2) : Math.max(die1, die2);
      const isCrit = chosenDie === 20;
      const total = chosenDie + effectiveBonus;
      const notationStr = `1d20${effectiveBonus >= 0 ? '+' : ''}${effectiveBonus} (${label})`;

      addDiceRoll({
        sender: senderName,
        characterName: character.name,
        notation: notationStr,
        result: {
          notation: `${notationStr} (${mode})${isCrit ? ' - CRITICAL HIT!' : ''}`,
          rolls: [{ sides: 20, value: chosenDie }],
          total,
          advDisRolls: [die1, die2] as [number, number],
          discardedValue: discardedDie,
        },
        rollType: mode,
        rollLabel: isCrit ? 'crit' : 'attack',
      });
    } else {
      const result = await roll('1d20');
      if (!result) return;

      const rawDie = result.rolls[0]?.value ?? 0;
      const dieValue = applyMojoCap(rawDie);
      const isCrit = dieValue === 20;
      const total = dieValue + effectiveBonus;
      const notationStr = `1d20${effectiveBonus >= 0 ? '+' : ''}${effectiveBonus} (${label})`;

      addDiceRoll({
        sender: senderName,
        characterName: character.name,
        notation: notationStr,
        result: {
          notation: notationStr,
          rolls: [{ sides: 20, value: dieValue }],
          total,
        },
        rollLabel: isCrit ? 'crit' : 'attack',
      });
    }
  };

  // Handle spell casting - looks up spell from Open5e and posts to chat
  const handleCastSpell = async (spellName: string) => {
    setCastingSpell(spellName);

    try {
      // Convert spell name to slug (e.g., "Fire Bolt" -> "fire-bolt")
      const slug = spellName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

      const spell = await getSpell(slug);

      // Resolve damage through character features (Agonizing Blast, Eldritch
      // Blast multi-beam, ability-mod cantrips, etc.) rather than passing raw.
      const cachedInfo = spellInfoCache[spellName];
      const resolved = character.spellcasting
        ? resolveSpellDamage({
            spellName,
            description: spell.desc,
            characterLevel,
            abilityMod: getModifier(character.abilityScores[character.spellcasting.ability]),
            features: character.features ?? [],
          })
        : null;

      const spellDamage = resolved
        ? {
            dice: resolved.dicePerHit,
            damageType: resolved.damageType,
            spellName,
            bonusPerHit: resolved.bonusPerHit || undefined,
            hitCount: resolved.hitCount > 1 ? resolved.hitCount : undefined,
            notes: resolved.notes.length > 0 ? resolved.notes : undefined,
          }
        : cachedInfo?.damage
          ? { dice: cachedInfo.damage.dice, damageType: cachedInfo.damage.damageType, spellName }
          : undefined;

      // For healing spells, pre-calculate the ability modifier so the button doesn't need character access
      const abilityMod = character.spellcasting
        ? getModifier(character.abilityScores[character.spellcasting.ability])
        : 0;
      const spellHealing = cachedInfo?.healing
        ? { dice: cachedInfo.healing.dice, spellName, abilityMod: cachedInfo.healing.addAbilityMod ? abilityMod : 0 }
        : undefined;

      addSpellLookup({
        sender: senderName,
        spell,
        spellDamage,
        spellHealing,
      });
    } catch (error) {
      console.error(`Failed to look up spell "${spellName}":`, error);
      // API lookup failed — post a basic spell reference instead
      addMessage({
        sender: senderName,
        characterName: character.name,
        message: `*casts **${spellName}***`,
        type: 'player',
      });
    } finally {
      setCastingSpell(null);
    }
  };

  // Handle spell attack — posts spell card then rolls attack
  const handleSpellAttack = async (spellName: string) => {
    if (!isReady || !character.spellcasting) return;

    // Post spell card first
    await handleCastSpell(spellName);

    // Calculate spell attack bonus: ability mod + proficiency
    const abilityMod = getModifier(character.abilityScores[character.spellcasting.ability]);
    const spellAttackBonus = abilityMod + character.proficiencyBonus;

    // Roll spell attack (reuses existing handleAttackRoll with advantage/disadvantage support)
    await handleAttackRoll(spellAttackBonus, `${spellName} (Spell Attack)`);
  };

  // Handle save-based spell cast — posts spell card then save prompt
  const handleSpellSave = async (spellName: string, saveAbility: string) => {
    if (!character.spellcasting) return;

    // Post spell card first
    await handleCastSpell(spellName);

    // Calculate spell save DC: 8 + ability mod + proficiency
    const abilityMod = getModifier(character.abilityScores[character.spellcasting.ability]);
    const saveDC = 8 + abilityMod + character.proficiencyBonus;

    addSavePrompt({
      casterName: character.name,
      spellName,
      dc: saveDC,
      ability: saveAbility,
    });
  };

  // Share a feature, equipment item, or resource to team chat
  const shareToChat = (title: string, description: string, source?: string) => {
    const parts = [`**${title}**`];
    if (source) parts.push(`_(${source})_`);
    if (description) parts.push(description);

    addMessage({
      sender: senderName,
      characterName: character.name,
      message: parts.join('\n'),
      type: 'player',
    });
  };

  // Image gallery modal
  const [galleryPurpose, setGalleryPurpose] = useState<ImagePurpose | null>(null);
  const [showLevelUpWizard, setShowLevelUpWizard] = useState(false);

  // Track which item was just shared for visual feedback
  const [sharedKey, setSharedKey] = useState<string | null>(null);
  const flashShared = (key: string) => {
    setSharedKey(key);
    setTimeout(() => setSharedKey(null), 1200);
  };

  // Calculate passive scores — normalize to lowercase for case-insensitive matching
  const skillProfs = (character.skillProficiencies ?? []).map((s) => s.toLowerCase());
  const skillExps = (character.skillExpertise ?? []).map((s) => s.toLowerCase());
  const passivePerception = 10 + getModifier(character.abilityScores.wisdom) +
    (skillProfs.includes('perception') ? character.proficiencyBonus : 0) +
    (skillExps.includes('perception') ? character.proficiencyBonus : 0);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: 'var(--color-background)',
      color: 'var(--color-text)',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        background: `linear-gradient(180deg, ${tokenColor}15 0%, transparent 100%)`,
        borderBottom: '1px solid rgba(100, 80, 120, 0.2)',
        position: 'relative',
      }}>
        {/* Notes button */}
        <button
          onClick={() => setShowNotes(true)}
          title="Campaign Notes"
          style={{
            position: 'absolute',
            top: 20,
            right: 24,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            opacity: character.playerNotes ? 1 : 0.4,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = character.playerNotes ? '1' : '0.4'; }}
        >
          <Icon name="scroll" size={18} color="#d4af37" />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Avatar portrait */}
          <div
            onClick={() => setGalleryPurpose('profile')}
            title="Change profile image"
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              border: `2px solid ${tokenColor}50`,
              overflow: 'hidden',
              cursor: 'pointer',
              flexShrink: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = tokenColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${tokenColor}50`; }}
          >
            {character.avatarUrl ? (
              <img
                src={character.avatarUrl}
                alt={character.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{
                fontSize: 20,
                fontFamily: 'Georgia, serif',
                color: `${tokenColor}80`,
                fontWeight: 300,
              }}>
                {character.name.slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 300,
              fontFamily: 'var(--font-display)',
              letterSpacing: '2px',
            }}>
              {character.name}
            </h2>
            <div style={{
              fontSize: 12,
              marginTop: 4,
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
            }}>
              {character.race} {character.background && `· ${character.background}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {character.classes.map((c, i) => (
            <div
              key={i}
              style={{
                padding: '4px 10px',
                backgroundColor: 'rgba(0,0,0,0.4)',
                borderRadius: '2px',
                fontSize: 10,
                border: `1px solid ${tokenColor}30`,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name={getClassIcon(c.name)} size={10} color={tokenColor} />
              {c.name} {c.level}{c.subclass && <span style={{ opacity: 0.7 }}> · {c.subclass}</span>}
            </div>
          ))}
        </div>
        {/* XP Bar + Level Up Button */}
        {!character.isNpc && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 3 }}>
                <span>XP: {(character.xp ?? 0).toLocaleString()}</span>
                <span>
                  {(() => {
                    const progress = getXpProgress(character);
                    return progress.nextThreshold ? `/ ${progress.nextThreshold.toLocaleString()}` : 'MAX';
                  })()}
                </span>
              </div>
              <div style={{ height: 4, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${getXpProgress(character).percentage}%`,
                  backgroundColor: character.pendingLevelUp ? '#4ade80' : '#d4af37',
                  borderRadius: 2,
                  transition: 'width 0.3s',
                }} />
              </div>
            </div>
            {character.pendingLevelUp && (
              <button
                onClick={() => setShowLevelUpWizard(true)}
                style={{
                  padding: '5px 12px',
                  background: 'linear-gradient(135deg, #d4af37, #b8962e)',
                  color: '#1a1520',
                  border: 'none',
                  borderRadius: 3,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Georgia, serif',
                }}
              >
                LEVEL UP
              </button>
            )}
          </div>
        )}
        {/* Token image selector */}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}
        >
          <div
            onClick={() => setGalleryPurpose('token')}
            title="Set custom token image"
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: `1px solid rgba(100, 80, 120, 0.3)`,
              overflow: 'hidden',
              cursor: 'pointer',
              flexShrink: 0,
              backgroundColor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d4af37'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(100, 80, 120, 0.3)'; }}
          >
            {character.tokenImageUrl ? (
              <img
                src={character.tokenImageUrl}
                alt="Token"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Icon name="token" size={14} color="rgba(255,255,255,0.3)" />
            )}
          </div>
          <span
            onClick={() => setGalleryPurpose('token')}
            style={{
              fontSize: 10,
              color: 'rgba(255,255,255,0.35)',
              cursor: 'pointer',
              fontFamily: 'Georgia, serif',
            }}
          >
            {character.tokenImageUrl ? 'Change token image' : 'Set token image'}
          </span>
        </div>
        {/* Player-facing vague score labels */}
        {(() => {
          const labels: { text: string; color: string }[] = [];
          if (isReputationChar && reputationScore >= 1) {
            const tier = getPersonalReputationTier(reputationScore);
            labels.push({ text: tier.playerLabel, color: tier.color });
          }
          if (isMojoChar && mojoScore >= 1) {
            const tier = getMojoTier(mojoScore);
            labels.push({ text: tier.playerLabel, color: tier.color });
          }
          if (charName.includes('dax')) {
            const invScore = character.inventorInspiration ?? 0;
            if (invScore >= 1) {
              const tier = getInventorTier(invScore);
              labels.push({ text: tier.playerLabel, color: tier.color });
            }
          }
          if (labels.length === 0) return null;
          return (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              {labels.map((l, i) => (
                <div key={i} style={{
                  padding: '3px 10px',
                  fontSize: 9,
                  fontStyle: 'italic',
                  color: l.color,
                  background: `${l.color}10`,
                  border: `1px solid ${l.color}25`,
                  borderRadius: 2,
                  letterSpacing: 0.5,
                }}>
                  {l.text}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Combat Stats Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          padding: '12px',
          marginBottom: 12,
          background: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
          border: '1px solid rgba(100, 80, 120, 0.2)',
        }}>
          <StatBox label="Armor Class" value={character.armorClass} />
          <StatBox label="Hit Points" value={character.currentHp} subValue={`/ ${character.maxHp}`} />
          <StatBox label="Speed" value={`${character.speed}'`} />
          <StatBox label="Prof. Bonus" value={`+${character.proficiencyBonus}`} />
          <StatBox label="Passive Perception" value={passivePerception} />
          {/* Initiative — clickable to roll */}
          <div
            onClick={handleInitiativeRoll}
            title={`Roll Initiative (1d20${formatModifier(getModifier(character.abilityScores.dexterity))})`}
            style={{
              textAlign: 'center',
              padding: '8px 12px',
              cursor: 'pointer',
              borderRadius: 4,
              border: currentInitiative > 0
                ? `1px solid ${tokenColor}60`
                : '1px solid rgba(100, 80, 120, 0.3)',
              background: currentInitiative > 0
                ? `${tokenColor}15`
                : 'rgba(100, 80, 120, 0.1)',
              transition: 'all 0.15s',
            }}
          >
            <div style={{
              fontSize: 24,
              fontWeight: 300,
              fontFamily: 'var(--font-display)',
              color: currentInitiative > 0 ? tokenColor : 'var(--color-text-muted)',
            }}>
              {currentInitiative > 0 ? currentInitiative : '—'}
            </div>
            <div style={{
              fontSize: 9,
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginTop: 4,
            }}>
              Initiative
            </div>
          </div>
        </div>

        {/* HP Controls */}
        <Section title="Hit Points" icon="heart" accentColor={tokenColor}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ height: 8, backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{
                width: `${hpPercent}%`,
                height: '100%',
                background: hpPercent >= 50 ? 'linear-gradient(90deg, var(--color-success), #e8e8e8)' : hpPercent >= 25 ? 'linear-gradient(90deg, var(--color-error), var(--color-warning))' : 'linear-gradient(90deg, #4a0000, var(--color-error))',
                transition: 'width 0.3s',
              }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[-10, -5, -1].map((amt) => (
              <button
                key={amt}
                onClick={() => damageCharacter(character.id, Math.abs(amt))}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  background: 'linear-gradient(180deg, rgba(139, 0, 0, 0.6) 0%, rgba(74, 0, 0, 0.8) 100%)',
                  color: 'rgba(255, 200, 200, 0.9)',
                  border: '1px solid rgba(139, 0, 0, 0.4)',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {amt}
              </button>
            ))}
            <div style={{ width: 8 }} />
            {[1, 5, 10].map((amt) => (
              <button
                key={amt}
                onClick={() => healCharacter(character.id, amt)}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  background: 'linear-gradient(180deg, rgba(100, 160, 100, 0.4) 0%, rgba(60, 100, 60, 0.6) 100%)',
                  color: 'rgba(200, 255, 200, 0.9)',
                  border: '1px solid rgba(100, 160, 100, 0.4)',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                +{amt}
              </button>
            ))}
          </div>
          {character.tempHp > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(100, 200, 255, 0.8)' }}>
              Temp HP: +{character.tempHp}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--color-text-muted)' }}>
            Hit Dice: {character.hitDice} ({character.hitDiceRemaining} remaining)
          </div>
        </Section>

        {/* Combat Actions */}
        <Section title="Combat Actions" icon="attack" accentColor={tokenColor}>
          {/* Attack Buttons */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ flex: 1, fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Attacks
              </div>
              {/* Advantage/Disadvantage Toggle */}
              <div style={{ display: 'flex', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 3, overflow: 'hidden' }}>
                {(['disadvantage', 'normal', 'advantage'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setAttackRollMode(mode)}
                    style={{
                      padding: '3px 8px',
                      fontSize: 9,
                      fontWeight: attackRollMode === mode ? 600 : 400,
                      backgroundColor:
                        attackRollMode === mode
                          ? mode === 'advantage' ? '#22c55e'
                          : mode === 'disadvantage' ? '#dc143c'
                          : 'rgba(192,192,192,0.5)'
                          : 'transparent',
                      color: attackRollMode === mode ? '#0a080c' : 'var(--color-text-muted)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {mode === 'disadvantage' ? 'DIS' : mode === 'advantage' ? 'ADV' : '—'}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const weapons = getCharacterWeapons(character.equipment ?? []);
              if (weapons.length === 0) {
                // Fallback: generic melee/ranged buttons (no weapons detected)
                const strMod = getModifier(character.abilityScores.strength);
                const dexMod = getModifier(character.abilityScores.dexterity);
                return (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleAttackRoll(strMod + character.proficiencyBonus, 'Melee Attack')}
                      disabled={!isReady}
                      style={{
                        flex: 1, padding: '12px 10px',
                        background: 'linear-gradient(180deg, rgba(139, 0, 0, 0.5) 0%, rgba(80, 0, 0, 0.7) 100%)',
                        color: '#ffd700', border: '1px solid rgba(212, 175, 55, 0.5)', borderRadius: '2px',
                        cursor: isReady ? 'pointer' : 'not-allowed', opacity: isReady ? 1 : 0.5,
                        fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                    >
                      <Icon name="sword" size={14} color="#ffd700" />
                      Melee +{strMod + character.proficiencyBonus}
                    </button>
                    <button
                      onClick={() => handleAttackRoll(dexMod + character.proficiencyBonus, 'Ranged Attack')}
                      disabled={!isReady}
                      style={{
                        flex: 1, padding: '12px 10px',
                        background: 'linear-gradient(180deg, rgba(0, 80, 60, 0.5) 0%, rgba(0, 50, 40, 0.7) 100%)',
                        color: '#90ee90', border: '1px solid rgba(144, 238, 144, 0.4)', borderRadius: '2px',
                        cursor: isReady ? 'pointer' : 'not-allowed', opacity: isReady ? 1 : 0.5,
                        fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                    >
                      <Icon name="target" size={14} color="#90ee90" />
                      Ranged +{dexMod + character.proficiencyBonus}
                    </button>
                  </div>
                );
              }
              // Per-weapon attack buttons
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {weapons.map((weapon, i) => {
                    const attackBonus = getWeaponAttackBonus(weapon, character.abilityScores, character.proficiencyBonus, getModifier);
                    const damageStr = getWeaponDamageNotation(weapon, character.abilityScores, getModifier);
                    const versatileDamageStr = weapon.versatileDamage
                      ? getWeaponDamageNotation(weapon, character.abilityScores, getModifier, true)
                      : null;
                    const isMelee = weapon.range === 'melee';
                    const bgGrad = isMelee
                      ? 'linear-gradient(180deg, rgba(139, 0, 0, 0.5) 0%, rgba(80, 0, 0, 0.7) 100%)'
                      : 'linear-gradient(180deg, rgba(0, 80, 60, 0.5) 0%, rgba(0, 50, 40, 0.7) 100%)';
                    const textColor = isMelee ? '#ffd700' : '#90ee90';
                    const borderColor = isMelee ? 'rgba(212, 175, 55, 0.5)' : 'rgba(144, 238, 144, 0.4)';

                    return (
                      <div key={i} style={{ display: 'flex', gap: 4 }}>
                        {/* Attack roll button */}
                        <button
                          onClick={() => handleAttackRoll(attackBonus, weapon.name)}
                          disabled={!isReady}
                          style={{
                            flex: 1, padding: '8px 10px', background: bgGrad,
                            color: textColor, border: `1px solid ${borderColor}`, borderRadius: '2px',
                            cursor: isReady ? 'pointer' : 'not-allowed', opacity: isReady ? 1 : 0.5,
                            fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
                          }}
                        >
                          <Icon name={isMelee ? 'sword' : 'target'} size={12} color={textColor} />
                          <span style={{ flex: 1, textAlign: 'left' }}>{weapon.name}</span>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>+{attackBonus}</span>
                        </button>
                        {/* Damage roll button */}
                        <button
                          onClick={() => {
                            const abilityMod = getWeaponAbilityMod(weapon, character.abilityScores, getModifier);
                            const totalMod = abilityMod + weapon.magicBonus + (echoEffects?.damageModifier ?? 0);
                            const modStr = totalMod >= 0 ? `+${totalMod}` : `${totalMod}`;
                            handleRoll(`${weapon.damage}${modStr}`, `${weapon.damage}${modStr}`, 'normal', 'damage');
                          }}
                          disabled={!isReady}
                          style={{
                            padding: '8px 8px',
                            background: 'linear-gradient(180deg, rgba(180, 80, 20, 0.4) 0%, rgba(120, 50, 10, 0.6) 100%)',
                            color: 'rgba(255, 200, 150, 0.9)', border: '1px solid rgba(180, 80, 20, 0.4)', borderRadius: '2px',
                            cursor: isReady ? 'pointer' : 'not-allowed', opacity: isReady ? 1 : 0.5,
                            fontSize: 10, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
                          }}
                          title={`${damageStr} ${weapon.damageType}`}
                        >
                          {damageStr}
                        </button>
                        {/* Versatile damage button */}
                        {versatileDamageStr && (
                          <button
                            onClick={() => {
                              const abilityMod = getWeaponAbilityMod(weapon, character.abilityScores, getModifier);
                              const totalMod = abilityMod + weapon.magicBonus + (echoEffects?.damageModifier ?? 0);
                              const modStr = totalMod >= 0 ? `+${totalMod}` : `${totalMod}`;
                              handleRoll(`${weapon.versatileDamage}${modStr}`, `${weapon.versatileDamage}${modStr}`, 'normal', 'damage');
                            }}
                            disabled={!isReady}
                            style={{
                              padding: '8px 6px',
                              background: 'linear-gradient(180deg, rgba(180, 80, 20, 0.3) 0%, rgba(120, 50, 10, 0.5) 100%)',
                              color: 'rgba(255, 200, 150, 0.7)', border: '1px solid rgba(180, 80, 20, 0.3)', borderRadius: '2px',
                              cursor: isReady ? 'pointer' : 'not-allowed', opacity: isReady ? 1 : 0.5,
                              fontSize: 9, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
                            }}
                            title={`${versatileDamageStr} ${weapon.damageType} (two-handed)`}
                          >
                            {versatileDamageStr}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Echo Knight: Manifest Echo */}
          {character.classes.some(c => c.subclass === 'Echo Knight') && (
            <EchoKnightControls character={character} />
          )}

          {/* D20 Rolls with Advantage/Disadvantage */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>
              D20 Roll
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => handleRoll('1d20', 'd20', 'disadvantage')}
                disabled={!isReady}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: 'linear-gradient(180deg, rgba(139, 0, 0, 0.4) 0%, rgba(74, 0, 0, 0.6) 100%)',
                  color: 'rgba(255, 180, 180, 0.9)',
                  border: '1px solid rgba(139, 0, 0, 0.4)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  opacity: isReady ? 1 : 0.5,
                  fontSize: 10,
                }}
              >
                Disadv
              </button>
              <button
                onClick={() => handleRoll('1d20', 'd20')}
                disabled={!isReady}
                style={{
                  flex: 2,
                  padding: '10px 8px',
                  background: 'linear-gradient(180deg, rgba(100, 80, 120, 0.5) 0%, rgba(60, 50, 80, 0.7) 100%)',
                  color: '#e8e4f0',
                  border: `1px solid ${tokenColor}60`,
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  opacity: isReady ? 1 : 0.5,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                d20
              </button>
              <button
                onClick={() => handleRoll('1d20', 'd20', 'advantage')}
                disabled={!isReady}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: 'linear-gradient(180deg, rgba(0, 100, 50, 0.4) 0%, rgba(0, 60, 30, 0.6) 100%)',
                  color: 'rgba(180, 255, 200, 0.9)',
                  border: '1px solid rgba(0, 139, 69, 0.4)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  opacity: isReady ? 1 : 0.5,
                  fontSize: 10,
                }}
              >
                Adv
              </button>
            </div>
          </div>

          {/* Saving Throws */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>
              Saving Throws
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const).map((ability) => {
                const mod = getModifier(character.abilityScores[ability]);
                const isProficient = character.savingThrowProficiencies.includes(ability);
                const saveBonus = mod + (isProficient ? character.proficiencyBonus : 0) + (echoEffects?.saveModifier ?? 0);
                const bonusStr = saveBonus >= 0 ? `+${saveBonus}` : `${saveBonus}`;

                return (
                  <button
                    key={ability}
                    onClick={() => handleRoll(`1d20${bonusStr}`, `${ability.slice(0, 3).toUpperCase()} Save`, 'normal', 'save')}
                    disabled={!isReady}
                    style={{
                      flex: '1 1 30%',
                      padding: '8px 6px',
                      background: isProficient
                        ? `linear-gradient(180deg, ${tokenColor}30 0%, ${tokenColor}20 100%)`
                        : 'rgba(0, 0, 0, 0.3)',
                      color: isProficient ? tokenColor : 'rgba(160, 150, 180, 0.7)',
                      border: isProficient ? `1px solid ${tokenColor}50` : '1px solid rgba(100, 80, 120, 0.2)',
                      borderRadius: '2px',
                      cursor: isReady ? 'pointer' : 'not-allowed',
                      opacity: isReady ? 1 : 0.5,
                      fontSize: 10,
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {ability.slice(0, 3).toUpperCase()} {bonusStr}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Damage Dice */}
          <div>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>
              Damage Dice
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['1d4', '1d6', '1d8', '1d10', '1d12', '2d6'].map((die) => (
                <button
                  key={die}
                  onClick={() => handleRoll(die, die, 'normal', 'damage')}
                  disabled={!isReady}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    background: 'linear-gradient(180deg, rgba(180, 80, 20, 0.4) 0%, rgba(120, 50, 10, 0.6) 100%)',
                    color: 'rgba(255, 200, 150, 0.9)',
                    border: '1px solid rgba(180, 80, 20, 0.4)',
                    borderRadius: '2px',
                    cursor: isReady ? 'pointer' : 'not-allowed',
                    opacity: isReady ? 1 : 0.5,
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {die}
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* Ability Scores — click any box to roll saving throw */}
        <Section title="Ability Scores" icon="crystal" accentColor={tokenColor}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
            {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const).map((ability) => {
              const score = character.abilityScores[ability];
              const mod = getModifier(score);
              const isProficient = character.savingThrowProficiencies.includes(ability);
              const saveBonus = mod + (isProficient ? character.proficiencyBonus : 0);
              const notation = saveBonus >= 0 ? `1d20+${saveBonus}` : `1d20${saveBonus}`;
              const abilityShort = ability.slice(0, 3).toUpperCase();

              return (
                <button
                  key={ability}
                  onClick={() => handleRoll(notation, `${abilityShort} Save`, 'normal', 'save')}
                  title={`Roll ${ability} saving throw (${formatModifier(saveBonus)})${isProficient ? ' • proficient' : ''}`}
                  style={{
                    textAlign: 'center',
                    padding: '10px 4px 8px',
                    background: isProficient ? `${tokenColor}15` : 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                    border: isProficient ? `1px solid ${tokenColor}40` : '1px solid rgba(100, 80, 120, 0.1)',
                    cursor: 'pointer',
                    transition: 'transform 120ms ease, background 120ms ease',
                    width: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.background = isProficient ? `${tokenColor}25` : 'rgba(0,0,0,0.35)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.background = isProficient ? `${tokenColor}15` : 'rgba(0,0,0,0.2)';
                  }}
                >
                  <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                    {abilityShort}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 300, fontFamily: 'var(--font-display)' }}>
                    {score}
                  </div>
                  <div style={{
                    fontSize: 11,
                    fontFamily: 'var(--font-mono)',
                    color: mod >= 0 ? 'rgba(134, 239, 172, 0.9)' : 'rgba(248, 113, 113, 0.9)',
                    marginTop: 2,
                  }}>
                    {formatModifier(mod)}
                  </div>
                  {/* Save bonus — always shown; bold if proficient */}
                  <div style={{
                    fontSize: 9,
                    fontFamily: 'var(--font-mono)',
                    color: isProficient ? tokenColor : 'rgba(255,255,255,0.35)',
                    marginTop: 4,
                    fontWeight: isProficient ? 600 : 400,
                    letterSpacing: '0.4px',
                  }}>
                    SAVE {formatModifier(saveBonus)}
                  </div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* Skills */}
        <Section title="Skills" icon="star" accentColor={tokenColor}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px 16px' }}>
            {SKILLS.map((skill) => {
              const mod = getModifier(character.abilityScores[skill.ability]);
              const isProficient = skillProfs.includes(skill.name.toLowerCase());
              const isExpert = skillExps.includes(skill.name.toLowerCase());
              const profBonus = isProficient ? character.proficiencyBonus : 0;
              const expertBonus = isExpert ? character.proficiencyBonus : 0;
              const total = mod + profBonus + expertBonus;

              const notation = total >= 0 ? `1d20+${total}` : `1d20${total}`;
              return (
                <button
                  key={skill.name}
                  onClick={() => handleRoll(notation, `${skill.name}`, 'normal', 'check')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 8px',
                    background: isProficient ? 'rgba(255,255,255,0.05)' : 'transparent',
                    borderRadius: '2px',
                    border: 'none',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                  }}
                  title={`Roll ${skill.name} check (${formatModifier(total)})`}
                >
                  <div style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: isExpert ? tokenColor : isProficient ? 'rgba(255,255,255,0.5)' : 'transparent',
                    border: `1px solid ${isProficient ? 'rgba(255,255,255,0.5)' : 'rgba(100,80,120,0.3)'}`,
                  }} />
                  <span style={{
                    flex: 1,
                    fontSize: 10,
                    color: isProficient ? 'var(--color-text)' : 'var(--color-text-muted)',
                  }}>
                    {skill.name}
                  </span>
                  <span style={{
                    fontSize: 11,
                    fontFamily: 'var(--font-mono)',
                    color: total >= 0 ? 'rgba(134, 239, 172, 0.9)' : 'rgba(248, 113, 113, 0.9)',
                  }}>
                    {formatModifier(total)}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>

        {/* Features & Traits */}
        {character.features.length > 0 && (
          <Section title="Features & Traits" icon="sparkle" accentColor={tokenColor}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {character.features.map((feature, i) => {
                const key = `feature-${i}`;
                const isShared = sharedKey === key;
                return (
                  <div
                    key={i}
                    onClick={() => {
                      shareToChat(feature.name, feature.description, feature.source);
                      flashShared(key);
                    }}
                    style={{
                      padding: '10px 12px',
                      background: isShared ? `${tokenColor}25` : 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '2px',
                      borderLeft: `2px solid ${isShared ? tokenColor : `${tokenColor}60`}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    title="Click to share in team chat"
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: 4,
                    }}>
                      <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--color-text)' }}>
                        {feature.name}
                      </span>
                      <span style={{ fontSize: 9, color: isShared ? tokenColor : 'var(--color-text-muted)', fontStyle: 'italic' }}>
                        {isShared ? 'Shared!' : feature.source}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                      {feature.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Spellcasting */}
        {character.spellcasting && (
          <Section title="Spellcasting" icon="wand" accentColor={tokenColor}>
            <div style={{ marginBottom: 12 }}>
              {(() => {
                const abilityMod = getModifier(character.abilityScores[character.spellcasting!.ability]);
                const spellAttackBonus = abilityMod + character.proficiencyBonus;
                const saveDC = 8 + abilityMod + character.proficiencyBonus;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, fontSize: 10, color: 'var(--color-text-muted)' }}>
                    <span>Spellcasting Ability: <span style={{ color: 'var(--color-text)', textTransform: 'capitalize' }}>{character.spellcasting!.ability}</span></span>
                    <span>Attack: <span style={{ color: '#90ee90', fontFamily: 'var(--font-mono)' }}>{formatModifier(spellAttackBonus)}</span></span>
                    <span>Save DC: <span style={{ color: '#ffd700', fontFamily: 'var(--font-mono)' }}>{saveDC}</span></span>
                  </div>
                );
              })()}

              {/* Spell Slots — click a filled pip to spend, empty pip to restore.
                  Shift+click a level header to reset that level to full. */}
              {Object.keys(character.spellcasting.spellSlots).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Spell Slots</div>
                    <button
                      onClick={() => {
                        if (!character.spellcasting) return;
                        updateCharacter(character.id, {
                          spellcasting: {
                            ...character.spellcasting,
                            spellSlotsUsed: {},
                          },
                        });
                      }}
                      title="Long rest — restore all spell slots"
                      style={{
                        padding: '2px 8px', fontSize: 9, fontWeight: 600,
                        background: 'transparent', color: tokenColor,
                        border: `1px solid ${tokenColor}40`, borderRadius: 2,
                        cursor: 'pointer', letterSpacing: 0.5,
                      }}
                    >
                      LONG REST
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {Object.entries(character.spellcasting.spellSlots)
                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                      .map(([level, total]) => {
                        const used = character.spellcasting?.spellSlotsUsed[level] || 0;
                        const remaining = total - used;
                        return (
                          <div key={level} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                              fontSize: 9, color: 'var(--color-text-muted)',
                              width: 34, flexShrink: 0, letterSpacing: 0.5,
                            }}>
                              LVL {level}
                            </div>
                            <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                              {Array.from({ length: total }).map((_, i) => {
                                const isFilled = i < remaining;
                                return (
                                  <button
                                    key={i}
                                    onClick={() => {
                                      if (!character.spellcasting) return;
                                      const currentUsed = character.spellcasting.spellSlotsUsed[level] || 0;
                                      // Filled pip → spend (used +1). Empty pip → restore (used -1).
                                      const nextUsed = isFilled
                                        ? Math.min(total, currentUsed + 1)
                                        : Math.max(0, currentUsed - 1);
                                      updateCharacter(character.id, {
                                        spellcasting: {
                                          ...character.spellcasting,
                                          spellSlotsUsed: {
                                            ...character.spellcasting.spellSlotsUsed,
                                            [level]: nextUsed,
                                          },
                                        },
                                      });
                                    }}
                                    title={isFilled ? `Spend level ${level} slot` : `Restore level ${level} slot`}
                                    style={{
                                      width: 16, height: 16, padding: 0,
                                      background: isFilled ? tokenColor : 'rgba(0,0,0,0.4)',
                                      border: `1px solid ${isFilled ? tokenColor : 'rgba(100,80,120,0.4)'}`,
                                      borderRadius: '50%',
                                      cursor: 'pointer',
                                      transition: 'transform 100ms ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                  />
                                );
                              })}
                            </div>
                            <div style={{
                              fontSize: 10, fontFamily: 'var(--font-mono)',
                              color: remaining === 0 ? 'rgba(248, 113, 113, 0.7)' : 'var(--color-text-muted)',
                              minWidth: 28, textAlign: 'right',
                            }}>
                              {remaining}/{total}
                            </div>
                          </div>
                        );
                    })}
                  </div>
                </div>
              )}

              {/* Cantrips (from knownSpells, always shown) */}
              {character.spellcasting.knownSpells.length > 0 && character.spellcasting.preparedSpells.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6 }}>Cantrips</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {character.spellcasting.knownSpells.map((spellName, i) => {
                      const isCasting = castingSpell === spellName;
                      const spellInfo = spellInfoCache[spellName];
                      const attackType = spellInfo?.attackType;
                      const saveType = spellInfo?.saveType;
                      const abilityMod = character.spellcasting ? getModifier(character.abilityScores[character.spellcasting.ability]) : 0;
                      const spellAttackBonus = abilityMod + character.proficiencyBonus;
                      const saveDC = 8 + abilityMod + character.proficiencyBonus;

                      if (attackType) {
                        const isRanged = attackType === 'ranged';
                        const attackColor = isRanged ? 'rgba(0, 100, 100, 0.7)' : 'rgba(120, 20, 20, 0.7)';
                        const attackTextColor = isRanged ? '#7dd3d3' : '#e8a0a0';
                        const attackIcon: IconName = isRanged ? 'target' : 'sword';
                        return (
                          <div key={`cantrip-${i}`} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button
                              onClick={() => handleCastSpell(spellName)}
                              disabled={isCasting}
                              style={{
                                padding: '4px 8px',
                                background: isCasting ? `${tokenColor}40` : `${tokenColor}20`,
                                border: `1px solid ${tokenColor}50`,
                                borderRight: 'none',
                                borderRadius: '2px 0 0 2px',
                                fontSize: 10,
                                cursor: isCasting ? 'wait' : 'pointer',
                                color: 'var(--color-text)',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                              title={`Cast ${spellName}`}
                            >
                              <Icon name="wand" size={10} color={tokenColor} />
                              {spellName}
                              {isCasting && <span style={{ marginLeft: 4 }}>...</span>}
                            </button>
                            <button
                              onClick={() => handleSpellAttack(spellName)}
                              disabled={isCasting || !isReady}
                              style={{
                                padding: '4px 6px',
                                background: attackColor,
                                border: `1px solid ${attackTextColor}60`,
                                borderRadius: '0 2px 2px 0',
                                fontSize: 10,
                                fontFamily: 'var(--font-mono)',
                                cursor: isCasting || !isReady ? 'not-allowed' : 'pointer',
                                color: attackTextColor,
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3,
                                opacity: isCasting || !isReady ? 0.5 : 1,
                              }}
                              title={`${isRanged ? 'Ranged' : 'Melee'} spell attack ${formatModifier(spellAttackBonus)}`}
                            >
                              <Icon name={attackIcon} size={10} color={attackTextColor} />
                              {formatModifier(spellAttackBonus)}
                            </button>
                          </div>
                        );
                      }

                      if (saveType) {
                        const abilityShort = saveType.ability.slice(0, 3).toUpperCase();
                        return (
                          <div key={`cantrip-${i}`} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button
                              onClick={() => handleCastSpell(spellName)}
                              disabled={isCasting}
                              style={{
                                padding: '4px 8px',
                                background: isCasting ? `${tokenColor}40` : `${tokenColor}20`,
                                border: `1px solid ${tokenColor}50`,
                                borderRight: 'none',
                                borderRadius: '2px 0 0 2px',
                                fontSize: 10,
                                cursor: isCasting ? 'wait' : 'pointer',
                                color: 'var(--color-text)',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                              title={`Cast ${spellName}`}
                            >
                              <Icon name="wand" size={10} color={tokenColor} />
                              {spellName}
                              {isCasting && <span style={{ marginLeft: 4 }}>...</span>}
                            </button>
                            <button
                              onClick={() => handleSpellSave(spellName, saveType.ability)}
                              disabled={isCasting}
                              style={{
                                padding: '4px 6px',
                                background: 'rgba(180, 140, 20, 0.5)',
                                border: '1px solid rgba(212, 175, 55, 0.5)',
                                borderRadius: '0 2px 2px 0',
                                fontSize: 9,
                                fontFamily: 'var(--font-mono)',
                                cursor: isCasting ? 'not-allowed' : 'pointer',
                                color: '#ffd700',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3,
                                opacity: isCasting ? 0.5 : 1,
                              }}
                              title={`${spellName} — ${abilityShort} Save DC ${saveDC}`}
                            >
                              <Icon name="shield" size={10} color="#ffd700" />
                              DC {saveDC} {abilityShort}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <button
                          key={`cantrip-${i}`}
                          onClick={() => handleCastSpell(spellName)}
                          disabled={isCasting}
                          style={{
                            padding: '4px 8px',
                            background: isCasting ? `${tokenColor}40` : `${tokenColor}20`,
                            border: `1px solid ${tokenColor}50`,
                            borderRadius: '2px',
                            fontSize: 10,
                            cursor: isCasting ? 'wait' : 'pointer',
                            color: 'var(--color-text)',
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          title={`Cast ${spellName}`}
                        >
                          <Icon name="wand" size={10} color={tokenColor} />
                          {spellName}
                          {isCasting && <span style={{ marginLeft: 4 }}>...</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Known/Prepared Spells */}
              {(character.spellcasting.knownSpells.length > 0 || character.spellcasting.preparedSpells.length > 0) && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                    {character.spellcasting.preparedSpells.length > 0 ? 'Prepared Spells' : 'Known Spells'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(character.spellcasting.preparedSpells.length > 0
                      ? character.spellcasting.preparedSpells
                      : character.spellcasting.knownSpells
                    ).map((spellName, i) => {
                      const isCasting = castingSpell === spellName;
                      const spellInfo = spellInfoCache[spellName];
                      const attackType = spellInfo?.attackType;
                      const saveType = spellInfo?.saveType;
                      const abilityMod = character.spellcasting ? getModifier(character.abilityScores[character.spellcasting.ability]) : 0;
                      const spellAttackBonus = abilityMod + character.proficiencyBonus;
                      const saveDC = 8 + abilityMod + character.proficiencyBonus;

                      if (attackType) {
                        // Split button: cast (left) + attack roll (right)
                        const isRanged = attackType === 'ranged';
                        const attackColor = isRanged ? 'rgba(0, 100, 100, 0.7)' : 'rgba(120, 20, 20, 0.7)';
                        const attackTextColor = isRanged ? '#7dd3d3' : '#e8a0a0';
                        const attackIcon: IconName = isRanged ? 'target' : 'sword';
                        return (
                          <div key={i} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button
                              onClick={() => handleCastSpell(spellName)}
                              disabled={isCasting}
                              style={{
                                padding: '4px 8px',
                                background: isCasting ? `${tokenColor}40` : `${tokenColor}20`,
                                border: `1px solid ${tokenColor}50`,
                                borderRight: 'none',
                                borderRadius: '2px 0 0 2px',
                                fontSize: 10,
                                cursor: isCasting ? 'wait' : 'pointer',
                                color: 'var(--color-text)',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                              title={`Cast ${spellName}`}
                            >
                              <Icon name="wand" size={10} color={tokenColor} />
                              {spellName}
                              {isCasting && <span style={{ marginLeft: 4 }}>...</span>}
                            </button>
                            <button
                              onClick={() => handleSpellAttack(spellName)}
                              disabled={isCasting || !isReady}
                              style={{
                                padding: '4px 6px',
                                background: attackColor,
                                border: `1px solid ${attackTextColor}60`,
                                borderRadius: '0 2px 2px 0',
                                fontSize: 10,
                                fontFamily: 'var(--font-mono)',
                                cursor: isCasting || !isReady ? 'not-allowed' : 'pointer',
                                color: attackTextColor,
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3,
                                opacity: isCasting || !isReady ? 0.5 : 1,
                              }}
                              title={`${isRanged ? 'Ranged' : 'Melee'} spell attack ${formatModifier(spellAttackBonus)}`}
                            >
                              <Icon name={attackIcon} size={10} color={attackTextColor} />
                              {formatModifier(spellAttackBonus)}
                            </button>
                          </div>
                        );
                      }

                      if (saveType) {
                        // Split button: cast (left) + DC badge (right)
                        const abilityShort = saveType.ability.slice(0, 3).toUpperCase();
                        return (
                          <div key={i} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button
                              onClick={() => handleCastSpell(spellName)}
                              disabled={isCasting}
                              style={{
                                padding: '4px 8px',
                                background: isCasting ? `${tokenColor}40` : `${tokenColor}20`,
                                border: `1px solid ${tokenColor}50`,
                                borderRight: 'none',
                                borderRadius: '2px 0 0 2px',
                                fontSize: 10,
                                cursor: isCasting ? 'wait' : 'pointer',
                                color: 'var(--color-text)',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                              title={`Cast ${spellName}`}
                            >
                              <Icon name="wand" size={10} color={tokenColor} />
                              {spellName}
                              {isCasting && <span style={{ marginLeft: 4 }}>...</span>}
                            </button>
                            <button
                              onClick={() => handleSpellSave(spellName, saveType.ability)}
                              disabled={isCasting}
                              style={{
                                padding: '4px 6px',
                                background: 'rgba(180, 140, 20, 0.5)',
                                border: '1px solid rgba(212, 175, 55, 0.5)',
                                borderRadius: '0 2px 2px 0',
                                fontSize: 9,
                                fontFamily: 'var(--font-mono)',
                                cursor: isCasting ? 'not-allowed' : 'pointer',
                                color: '#ffd700',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3,
                                opacity: isCasting ? 0.5 : 1,
                              }}
                              title={`${spellName} — ${abilityShort} Save DC ${saveDC}`}
                            >
                              <Icon name="shield" size={10} color="#ffd700" />
                              DC {saveDC} {abilityShort}
                            </button>
                          </div>
                        );
                      }

                      // Utility spell: single cast button
                      return (
                        <button
                          key={i}
                          onClick={() => handleCastSpell(spellName)}
                          disabled={isCasting}
                          style={{
                            padding: '4px 8px',
                            background: isCasting
                              ? `${tokenColor}40`
                              : `${tokenColor}20`,
                            border: `1px solid ${tokenColor}50`,
                            borderRadius: '2px',
                            fontSize: 10,
                            cursor: isCasting ? 'wait' : 'pointer',
                            color: 'var(--color-text)',
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          title={`Cast ${spellName}`}
                        >
                          <Icon name="wand" size={10} color={tokenColor} />
                          {spellName}
                          {isCasting && <span style={{ marginLeft: 4 }}>...</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Resources */}
        {(character.resources ?? []).length > 0 && (
          <Section title="Resources" icon="crystal" accentColor={tokenColor}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(character.resources ?? []).map((resource, i) => {
                const key = `resource-${i}`;
                const isShared = sharedKey === key;
                return (
                  <div
                    key={i}
                    onClick={() => {
                      shareToChat(resource.name, `${resource.current}/${resource.max} remaining (recharges: ${resource.rechargeOn.replace('_', ' ')})`);
                      flashShared(key);
                    }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: isShared ? `${tokenColor}25` : 'rgba(0,0,0,0.3)',
                      borderRadius: '2px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    title="Click to share in team chat"
                  >
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--color-text)' }}>{resource.name}</div>
                      <div style={{ fontSize: 9, color: isShared ? tokenColor : 'var(--color-text-muted)', textTransform: 'capitalize' }}>
                        {isShared ? 'Shared!' : `Recharges: ${resource.rechargeOn.replace('_', ' ')}`}
                      </div>
                    </div>
                    <div style={{ fontSize: 16, fontFamily: 'var(--font-mono)' }}>
                      {resource.current}/{resource.max}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Equipment */}
        {(character.equipment ?? []).length > 0 && (
          <Section title="Equipment" icon="shield" accentColor={tokenColor}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(character.equipment ?? []).map((item, i) => {
                const key = `equip-${i}`;
                const isShared = sharedKey === key;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      shareToChat(item, '', 'Equipment');
                      flashShared(key);
                    }}
                    style={{
                      padding: '4px 10px',
                      background: isShared ? `${tokenColor}25` : 'rgba(0,0,0,0.3)',
                      border: `1px solid ${isShared ? tokenColor : 'rgba(100,80,120,0.2)'}`,
                      borderRadius: '2px',
                      fontSize: 10,
                      cursor: 'pointer',
                      color: 'var(--color-text)',
                      transition: 'all 0.2s',
                    }}
                    title="Click to share in team chat"
                  >
                    {isShared ? `${item} ✓` : item}
                  </button>
                );
              })}
            </div>
          </Section>
        )}

        {/* Background & Personality */}
        {(character.personalityTraits || character.ideals || character.bonds || character.flaws) && (
          <Section title="Personality" icon="heart" accentColor={tokenColor}>
            {character.personalityTraits && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>Personality Traits</div>
                <div style={{ fontSize: 11, color: 'var(--color-text)', fontStyle: 'italic' }}>{character.personalityTraits}</div>
              </div>
            )}
            {character.ideals && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>Ideals</div>
                <div style={{ fontSize: 11, color: 'var(--color-text)', fontStyle: 'italic' }}>{character.ideals}</div>
              </div>
            )}
            {character.bonds && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>Bonds</div>
                <div style={{ fontSize: 11, color: 'var(--color-text)', fontStyle: 'italic' }}>{character.bonds}</div>
              </div>
            )}
            {character.flaws && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>Flaws</div>
                <div style={{ fontSize: 11, color: 'var(--color-text)', fontStyle: 'italic' }}>{character.flaws}</div>
              </div>
            )}
          </Section>
        )}

        {/* Notes */}
        {character.notes && (
          <Section title="Notes" icon="edit" accentColor={tokenColor}>
            <div style={{ fontSize: 11, color: 'var(--color-text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {character.notes}
            </div>
          </Section>
        )}

        {/* Footer */}
        {character.playerName && (
          <div style={{
            marginTop: 8,
            padding: '12px',
            textAlign: 'center',
            fontSize: 10,
            color: 'var(--color-text-muted)',
            borderTop: '1px solid var(--color-border)',
            fontStyle: 'italic',
          }}>
            Played by <span style={{ color: 'var(--color-text-secondary)' }}>{character.playerName}</span>
          </div>
        )}
      </div>

      {/* Player Notes Modal */}
      {showNotes && (
        <PlayerNotesModal
          characterName={character.name}
          notes={character.playerNotes ?? ''}
          onSave={handleSaveNotes}
          onClose={() => setShowNotes(false)}
        />
      )}

      {/* Level-Up Wizard */}
      {showLevelUpWizard && (
        <LevelUpWizard
          characterId={character.id}
          onClose={() => setShowLevelUpWizard(false)}
        />
      )}

      {/* Image Gallery Modal */}
      {galleryPurpose && (
        <ImageGallery
          purpose={galleryPurpose}
          onSelect={(url) => {
            if (galleryPurpose === 'profile') {
              updateCharacter(character.id, { avatarUrl: url });
            } else if (galleryPurpose === 'token') {
              updateCharacter(character.id, { tokenImageUrl: url });
            }
          }}
          onClose={() => setGalleryPurpose(null)}
        />
      )}
    </div>
  );
}
